import { useCallback, useEffect, useState } from 'react';
import { settings as settingsApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Loading, Can, money } from '../components/Common';

const TABS = [
  { key: 'general', label: 'General' },
  { key: 'charges', label: 'Taxes & Charges' },
  { key: 'otp', label: 'OTP Mode' },
  { key: 'payment', label: 'Payment Gateway' },
  { key: 'app', label: 'Pricing & App' },
];

export default function Settings() {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();

  const [tab, setTab] = useState('general');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await settingsApi.get();
      setData(res.data.settings);
    } catch (err) {
      toast.error('Could not load settings', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Settings · UrbanClean'; }, []);

  if (loading) return <Loading label="Loading settings" />;
  if (!data) return null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">Platform configuration, OTP mode and payment gateway keys</div>
        </div>
      </div>

      <div className="toolbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'general' ? <GeneralTab data={data} onSaved={load} /> : null}
      {tab === 'charges' ? <ChargesTab data={data} onSaved={load} /> : null}
      {tab === 'otp' ? <OtpTab data={data} onSaved={load} /> : null}
      {tab === 'payment' ? <PaymentTab data={data} isSuperAdmin={isSuperAdmin} onSaved={load} /> : null}
      {tab === 'app' ? <AppTab data={data} onSaved={load} /> : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* OTP mode - the dev / live switch                                            */
/* -------------------------------------------------------------------------- */

function OtpTab({ data, onSaved }) {
  const toast = useToast();
  const [devEnabled, setDevEnabled] = useState(data.devOtpEnabled);
  const [code, setCode] = useState(data.devOtpCode || '123456');
  const [busy, setBusy] = useState(false);

  const save = async (nextEnabled = devEnabled) => {
    setBusy(true);
    try {
      const res = await settingsApi.otpMode({ devOtpEnabled: nextEnabled, devOtpCode: code });
      setDevEnabled(res.data.devOtpEnabled);
      toast.success(res.message);
      onSaved();
    } catch (err) {
      toast.error('Could not update OTP mode', err.message);
      setDevEnabled(data.devOtpEnabled);
    } finally {
      setBusy(false);
    }
  };

  const flip = (checked) => {
    setDevEnabled(checked);
    save(checked);
  };

  return (
    <div className="grid grid-2">
      <div className="card">
        <div className="card-head"><h3>OTP mode</h3></div>
        <div className="card-body">
          <div
            className="card mb"
            style={{
              background: devEnabled ? 'var(--amber-bg)' : 'var(--green-bg)',
              border: 'none',
              boxShadow: 'none',
            }}
          >
            <div className="card-body" style={{ padding: 14 }}>
              <div className="strong" style={{ color: devEnabled ? 'var(--amber)' : 'var(--green)' }}>
                {devEnabled ? '⚠ Development mode is ON' : '✓ Live mode is ON'}
              </div>
              <div style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.6 }}>
                {devEnabled ? (
                  <>
                    Every mobile number — customer and delivery partner — signs in with the fixed
                    code <strong className="mono">{data.devOtpCode}</strong>. No SMS is sent, so
                    testing costs nothing. <strong>Turn this off before going live.</strong>
                  </>
                ) : (
                  <>
                    A random 6-digit OTP is generated per sign-in and handed to the SMS gateway.
                    Make sure the SMS provider credentials are configured on the server.
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="field">
            <label className="switch">
              <input
                type="checkbox"
                checked={devEnabled}
                disabled={busy}
                onChange={(e) => flip(e.target.checked)}
              />
              <span className="track" />
              <span className="strong">Use a fixed development OTP</span>
            </label>
            <div className="hint">
              Toggling this takes effect immediately — no restart, no redeploy.
            </div>
          </div>

          <div className="field">
            <label>Development OTP code</label>
            <input
              className="input mono"
              value={code}
              maxLength={6}
              disabled={!devEnabled}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ fontSize: 19, letterSpacing: 3, maxWidth: 180 }}
            />
            <div className="hint">4 to 6 digits. Defaults to 123456.</div>
          </div>

          <Can permission="settings.update">
            <button className="btn btn-primary" onClick={() => save()} disabled={busy || !devEnabled}>
              {busy ? 'Saving…' : 'Save code'}
            </button>
          </Can>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h3>How sign-in works</h3></div>
        <div className="card-body" style={{ fontSize: 13, lineHeight: 1.8 }}>
          <p style={{ marginTop: 0 }}>
            Customers and delivery partners never use a password — they enter a mobile number,
            receive a code, and are signed in. Admin and staff accounts use email + password
            instead and are unaffected by this switch.
          </p>
          <div className="strong" style={{ marginTop: 14, color: 'var(--navy)' }}>Development mode</div>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            The API returns the code in the response and skips the SMS gateway entirely, so the
            apps prefill it and testers never wait for a message.
          </p>
          <div className="strong" style={{ marginTop: 14, color: 'var(--navy)' }}>Live mode</div>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            A random code is generated, stored with a 10-minute expiry and sent over SMS. The
            code is never returned in the API response.
          </p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Payment gateway - Xendit                                                    */
/* -------------------------------------------------------------------------- */

function PaymentTab({ data, isSuperAdmin, onSaved }) {
  const toast = useToast();
  const xendit = data.payment?.xendit || {};

  const [form, setForm] = useState({
    secretKey: '', publicKey: '', webhookToken: '',
    successRedirectUrl: xendit.successRedirectUrl || '',
    failureRedirectUrl: xendit.failureRedirectUrl || '',
    invoiceCurrency: xendit.invoiceCurrency || 'IDR',
    invoiceDurationSeconds: xendit.invoiceDurationSeconds || 86400,
  });
  const [isLive, setIsLive] = useState(Boolean(data.payment?.isLive));
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, isLive };
      // Blank secret fields mean "leave the saved key alone".
      if (!body.secretKey) delete body.secretKey;
      if (!body.publicKey) delete body.publicKey;
      if (!body.webhookToken) delete body.webhookToken;

      const res = await settingsApi.paymentGateway(body);
      toast.success(res.message);
      if (res.data.warning) toast.error('Check this', res.data.warning);
      setForm((f) => ({ ...f, secretKey: '', publicKey: '', webhookToken: '' }));
      onSaved();
    } catch (err) {
      toast.error('Could not save the gateway keys', err.message);
    } finally {
      setBusy(false);
    }
  };

  const webhookUrl = `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api')}/payments/webhook/xendit`;

  if (!isSuperAdmin) {
    return (
      <div className="card">
        <div className="empty">
          <div className="strong" style={{ fontSize: 15, color: 'var(--navy)' }}>Super Admin only</div>
          <div style={{ marginTop: 6 }}>Payment gateway keys can only be viewed and changed by the Super Admin.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Xendit credentials</h3>
          <span className={`badge badge-${xendit.isConfigured ? 'green' : 'amber'}`}>
            {xendit.isConfigured ? 'Configured' : 'Not configured'}
          </span>
        </div>
        <div className="card-body">
          <div className="field">
            <label className="switch">
              <input type="checkbox" checked={isLive} onChange={(e) => setIsLive(e.target.checked)} />
              <span className="track" />
              <span className="strong">{isLive ? 'Live keys' : 'Test keys'}</span>
            </label>
            <div className="hint">Flip this when you swap the test keys for production keys.</div>
          </div>

          <div className="field">
            <label>Secret key</label>
            <input
              className="input mono"
              type="password"
              value={form.secretKey}
              onChange={set('secretKey')}
              placeholder={xendit.secretKeyPreview || 'xnd_development_...'}
              autoComplete="off"
            />
            <div className="hint">
              {xendit.secretKeyPreview
                ? `Currently saved: ${xendit.secretKeyPreview} — leave blank to keep it.`
                : 'From the Xendit dashboard → Settings → API Keys.'}
            </div>
          </div>

          <div className="field">
            <label>Public key</label>
            <input
              className="input mono"
              type="password"
              value={form.publicKey}
              onChange={set('publicKey')}
              placeholder={xendit.publicKeyPreview || 'xnd_public_...'}
              autoComplete="off"
            />
          </div>

          <div className="field">
            <label>Webhook callback token</label>
            <input
              className="input mono"
              type="password"
              value={form.webhookToken}
              onChange={set('webhookToken')}
              placeholder={xendit.webhookTokenPreview || 'Callback verification token'}
              autoComplete="off"
            />
            <div className="hint">
              Xendit sends this as the <span className="mono">x-callback-token</span> header. Without it,
              payment callbacks are rejected and orders never settle.
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label>Invoice currency</label>
              <input className="input" value={form.invoiceCurrency} onChange={set('invoiceCurrency')} placeholder="IDR" />
              <div className="hint">Must match your Xendit account's currency.</div>
            </div>
            <div className="field">
              <label>Invoice expiry (seconds)</label>
              <input className="input" type="number" value={form.invoiceDurationSeconds} onChange={set('invoiceDurationSeconds')} />
            </div>
          </div>

          <div className="field">
            <label>Success redirect URL</label>
            <input className="input" value={form.successRedirectUrl} onChange={set('successRedirectUrl')} placeholder="urbanclean://payment/success" />
          </div>
          <div className="field">
            <label>Failure redirect URL</label>
            <input className="input" value={form.failureRedirectUrl} onChange={set('failureRedirectUrl')} placeholder="urbanclean://payment/failed" />
          </div>

          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save gateway settings'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h3>Setting Xendit up</h3></div>
        <div className="card-body" style={{ fontSize: 13, lineHeight: 1.75 }}>
          <ol style={{ paddingLeft: 18, margin: 0 }}>
            <li>In the Xendit dashboard open <strong>Settings → API Keys</strong> and create a key with
                <em> Money-in</em> write access.</li>
            <li>Paste the secret and public keys on the left and save.</li>
            <li>Open <strong>Settings → Webhooks</strong>, copy the callback verification token and paste
                it in the field on the left.</li>
            <li>Set the <strong>Invoices paid</strong> webhook URL to the address below.</li>
          </ol>

          <div className="field mt">
            <label>Webhook URL</label>
            <input className="input mono" readOnly value={webhookUrl} onFocus={(e) => e.target.select()} />
            <div className="hint">Use your public API domain here in production — not localhost.</div>
          </div>

          <div className="card" style={{ background: 'var(--blue-50)', border: 'none', boxShadow: 'none' }}>
            <div className="card-body" style={{ padding: 13, fontSize: 12.5 }}>
              <div className="strong" style={{ color: 'var(--navy)' }}>How a payment flows</div>
              <div className="muted" style={{ marginTop: 5, lineHeight: 1.7 }}>
                The app asks the API for a checkout → the API creates a Xendit hosted invoice and returns
                its URL → the customer pays on that page → Xendit calls the webhook above → the API verifies
                the token, marks the order paid and pushes the update to this panel over the live socket.
              </div>
            </div>
          </div>

          <div className="hint" style={{ marginTop: 12 }}>
            Keys are stored write-only: the panel only ever shows a masked preview, and only the
            Super Admin can change them.
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Taxes & charges                                                             */
/* -------------------------------------------------------------------------- */

const BLANK_CHARGE = {
  name: '', type: 'percent', value: 0, appliesOn: 'taxable', isTax: true, isActive: true,
};

/**
 * Everything added to a bill after the item total.
 *
 * Each row is a percentage or a fixed amount, applied either to the item total
 * or to the full taxable amount (items + express + delivery − discount). The
 * live preview underneath uses a sample basket so the effect of a change is
 * visible before saving. Orders store the breakdown they were billed with, so
 * editing these rates never rewrites a past invoice.
 */
function ChargesTab({ data, onSaved }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows((data.charges || []).map((c) => ({ ...c })));
  }, [data]);

  const set = (index, key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setRows((list) => list.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  };

  const addRow = () => setRows((list) => [...list, { ...BLANK_CHARGE, sortOrder: list.length }]);
  const removeRow = (index) => setRows((list) => list.filter((_, i) => i !== index));

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    setRows((list) => {
      const next = [...list];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((r, i) => ({ ...r, sortOrder: i }));
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await settingsApi.charges({
        charges: rows.map((r, i) => ({ ...r, value: Number(r.value), sortOrder: i })),
      });
      toast.success(res.message);
      onSaved();
    } catch (err) {
      toast.error('Could not save the charges', err.message);
    } finally {
      setBusy(false);
    }
  };

  // Sample basket, mirroring exactly how the server prices an order.
  const sample = { subtotal: 1000, express: 300, delivery: 0, discount: 100 };
  const taxable = sample.subtotal + sample.express + sample.delivery - sample.discount;
  let previewTotal = taxable;
  const previewRows = rows
    .filter((r) => r.isActive !== false && r.name)
    .map((r) => {
      const base = r.appliesOn === 'subtotal' ? sample.subtotal : taxable;
      const amount = r.type === 'flat'
        ? Math.round(Number(r.value) || 0)
        : Math.round((base * (Number(r.value) || 0)) / 100);
      previewTotal += amount;
      return { name: r.name, amount, type: r.type, value: r.value };
    });

  return (
    <div className="grid grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Taxes &amp; charges on every bill</h3>
          <Can permission="settings.update">
            <button className="btn btn-sm btn-ghost" onClick={addRow}>+ Add a row</button>
          </Can>
        </div>
        <div className="card-body">
          {rows.length === 0 ? (
            <div className="empty" style={{ padding: 30 }}>
              No taxes or charges. Bills will show just the item total.
            </div>
          ) : (
            rows.map((row, index) => (
              <div key={index} className="perm-module" style={{ marginBottom: 12 }}>
                <div className="perm-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <input
                      className="input"
                      value={row.name}
                      onChange={set(index, 'name')}
                      placeholder="GST, Service fee, Packaging…"
                      style={{ maxWidth: 210 }}
                    />
                    <span className="badge badge-gray">
                      {row.type === 'flat' ? `Flat ${row.value}` : `${row.value}%`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm btn-ghost" onClick={() => move(index, -1)} disabled={index === 0} title="Move up">↑</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => move(index, 1)} disabled={index === rows.length - 1} title="Move down">↓</button>
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }} onClick={() => removeRow(index)}>Remove</button>
                  </div>
                </div>

                <div style={{ padding: '12px 14px' }}>
                  <div className="row">
                    <div className="field" style={{ marginBottom: 10 }}>
                      <label>Type</label>
                      <select className="select" value={row.type} onChange={set(index, 'type')}>
                        <option value="percent">Percentage</option>
                        <option value="flat">Fixed amount</option>
                      </select>
                    </div>
                    <div className="field" style={{ marginBottom: 10 }}>
                      <label>{row.type === 'flat' ? 'Amount' : 'Percent'}</label>
                      <input className="input" type="number" value={row.value} onChange={set(index, 'value')} />
                    </div>
                    {row.type === 'percent' ? (
                      <div className="field" style={{ marginBottom: 10 }}>
                        <label>Calculated on</label>
                        <select className="select" value={row.appliesOn} onChange={set(index, 'appliesOn')}>
                          <option value="taxable">Items + express + delivery − discount</option>
                          <option value="subtotal">Item total only</option>
                        </select>
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <label className="switch">
                      <input type="checkbox" checked={row.isActive !== false} onChange={set(index, 'isActive')} />
                      <span className="track" /><span>Applied to bills</span>
                    </label>
                    <label className="switch">
                      <input type="checkbox" checked={row.isTax !== false} onChange={set(index, 'isTax')} />
                      <span className="track" /><span>Counts as tax on the GST invoice</span>
                    </label>
                  </div>
                </div>
              </div>
            ))
          )}

          <Can permission="settings.update">
            <button className="btn btn-primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save taxes & charges'}
            </button>
          </Can>
        </div>
      </div>

      <div className="card" style={{ alignSelf: 'start' }}>
        <div className="card-head"><h3>Live preview</h3></div>
        <div className="card-body">
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 14 }}>
            How a sample order's bill would look with the rows on the left.
          </div>

          <BillRow label="Item total" value={money(sample.subtotal)} />
          <BillRow label="Express charge" value={money(sample.express)} />
          <BillRow label="Delivery" value="FREE" tone="green" />
          <BillRow label="Coupon discount" value={`− ${money(sample.discount)}`} tone="green" />

          <div style={{ borderTop: '1px solid var(--line)', margin: '10px 0', paddingTop: 10 }}>
            {previewRows.length === 0 ? (
              <div className="muted" style={{ fontSize: 12.5 }}>No charges applied.</div>
            ) : previewRows.map((r, i) => (
              <BillRow
                key={i}
                label={`${r.name}${r.type === 'percent' ? ` (${r.value}%)` : ''}`}
                value={money(r.amount)}
              />
            ))}
          </div>

          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            <BillRow label="Total payable" value={money(previewTotal)} big />
          </div>

          <div className="hint" style={{ marginTop: 14 }}>
            Rows apply top to bottom. Orders keep the breakdown they were billed
            with, so changing a rate here never alters an existing invoice.
          </div>
        </div>
      </div>
    </div>
  );
}

function BillRow({ label, value, tone, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
      <span className={big ? 'strong' : 'muted'} style={{ fontSize: big ? 14.5 : 13 }}>{label}</span>
      <span
        className="strong"
        style={{ fontSize: big ? 16 : 13.5, color: tone === 'green' ? 'var(--green)' : 'var(--navy)' }}
      >
        {value}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* General + pricing                                                           */
/* -------------------------------------------------------------------------- */

function GeneralTab({ data, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    appName: data.appName || '',
    supportMobile: data.supportMobile || '',
    supportEmail: data.supportEmail || '',
    whatsappNumber: data.whatsappNumber || '',
    gstNumber: data.gstNumber || '',
    currencySymbol: data.currencySymbol || '₹',
    maintenanceMode: Boolean(data.maintenanceMode),
  });
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await settingsApi.update(form);
      toast.success('Settings saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: 720 }}>
      <div className="card-head"><h3>General</h3></div>
      <div className="card-body">
        <div className="row">
          <div className="field"><label>App name</label><input className="input" value={form.appName} onChange={set('appName')} /></div>
          <div className="field"><label>Currency symbol</label><input className="input" value={form.currencySymbol} onChange={set('currencySymbol')} /></div>
        </div>
        <div className="row">
          <div className="field"><label>Support mobile</label><input className="input" value={form.supportMobile} onChange={set('supportMobile')} /></div>
          <div className="field"><label>Support email</label><input className="input" value={form.supportEmail} onChange={set('supportEmail')} /></div>
        </div>
        <div className="row">
          <div className="field">
            <label>WhatsApp number</label>
            <input className="input" value={form.whatsappNumber} onChange={set('whatsappNumber')} placeholder="With country code" />
            <div className="hint">Powers the "WhatsApp support" button in the customer app.</div>
          </div>
          <div className="field"><label>GST number</label><input className="input" value={form.gstNumber} onChange={set('gstNumber')} /></div>
        </div>

        <div className="field">
          <label className="switch">
            <input type="checkbox" checked={form.maintenanceMode} onChange={set('maintenanceMode')} />
            <span className="track" />
            <span className="strong">Maintenance mode</span>
          </label>
          <div className="hint">The mobile apps show a maintenance screen and block new bookings.</div>
        </div>

        <Can permission="settings.update">
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
        </Can>
      </div>
    </div>
  );
}

function AppTab({ data, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    deliveryCharge: data.deliveryCharge,
    freeDeliveryAbove: data.freeDeliveryAbove,
    minOrderValue: data.minOrderValue,
    expressChargeType: data.expressChargeType || 'percent',
    expressChargePercent: data.expressChargePercent,
    expressChargeFlat: data.expressChargeFlat,
    expressChargeMax: data.expressChargeMax,
    standardTurnaroundHours: data.standardTurnaroundHours,
    expressTurnaroundHours: data.expressTurnaroundHours,
    referralRewardReferrer: data.referralRewardReferrer,
    referralRewardReferee: data.referralRewardReferee,
    codEnabled: Boolean(data.codEnabled),
    onlinePaymentEnabled: Boolean(data.onlinePaymentEnabled),
    walletEnabled: Boolean(data.walletEnabled),
    forceUpdate: Boolean(data.forceUpdate),
    androidVersion: data.androidVersion,
    iosVersion: data.iosVersion,
  });
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await settingsApi.update(form);
      toast.success('Charges updated');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-2">
      <div className="card">
        <div className="card-head"><h3>Charges &amp; limits</h3></div>
        <div className="card-body">
          <div className="row">
            <div className="field">
              <label>Express charge type</label>
              <select className="select" value={form.expressChargeType} onChange={set('expressChargeType')}>
                <option value="percent">Percentage of the basket</option>
                <option value="flat">Flat amount</option>
              </select>
            </div>
          </div>

          {form.expressChargeType === 'flat' ? (
            <div className="field">
              <label>Express flat charge</label>
              <input className="input" type="number" value={form.expressChargeFlat} onChange={set('expressChargeFlat')} />
              <div className="hint">Added once to any express order, whatever the basket size.</div>
            </div>
          ) : (
            <div className="row">
              <div className="field">
                <label>Express surcharge %</label>
                <input className="input" type="number" value={form.expressChargePercent} onChange={set('expressChargePercent')} />
              </div>
              <div className="field">
                <label>Cap the surcharge at</label>
                <input className="input" type="number" value={form.expressChargeMax} onChange={set('expressChargeMax')} />
                <div className="hint">0 means no cap.</div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="field">
              <label>Delivery charge</label>
              <input className="input" type="number" value={form.deliveryCharge} onChange={set('deliveryCharge')} />
            </div>
            <div className="field">
              <label>Free delivery above</label>
              <input className="input" type="number" value={form.freeDeliveryAbove} onChange={set('freeDeliveryAbove')} />
              <div className="hint">Baskets at or over this amount ship free.</div>
            </div>
          </div>
          <div className="row">
            <div className="field"><label>Minimum order value</label><input className="input" type="number" value={form.minOrderValue} onChange={set('minOrderValue')} /></div>
            <div className="field"><label>Standard turnaround (hrs)</label><input className="input" type="number" value={form.standardTurnaroundHours} onChange={set('standardTurnaroundHours')} /></div>
          </div>
          <div className="row">
            <div className="field"><label>Referral reward — referrer</label><input className="input" type="number" value={form.referralRewardReferrer} onChange={set('referralRewardReferrer')} /></div>
            <div className="field"><label>Referral reward — new user</label><input className="input" type="number" value={form.referralRewardReferee} onChange={set('referralRewardReferee')} /></div>
          </div>

          <div className="field">
            <label>Pickup slots &amp; closed days</label>
            <div className="hint" style={{ marginTop: 0 }}>
              Time slots, weekly offs and holidays are managed together under
              <strong> Holidays &amp; Schedule</strong>.
            </div>
          </div>

          <Can permission="settings.update">
            <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </Can>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h3>Payment methods &amp; app version</h3></div>
        <div className="card-body">
          {[
            ['codEnabled', 'Cash on delivery'],
            ['onlinePaymentEnabled', 'Online payment (Xendit)'],
            ['walletEnabled', 'Wallet payments'],
            ['forceUpdate', 'Force app update'],
          ].map(([key, label]) => (
            <div className="field" key={key}>
              <label className="switch">
                <input type="checkbox" checked={form[key]} onChange={set(key)} />
                <span className="track" />
                <span>{label}</span>
              </label>
            </div>
          ))}

          <div className="row">
            <div className="field"><label>Android version</label><input className="input" value={form.androidVersion} onChange={set('androidVersion')} /></div>
            <div className="field"><label>iOS version</label><input className="input" value={form.iosVersion} onChange={set('iosVersion')} /></div>
          </div>

          <Can permission="settings.update">
            <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </Can>
        </div>
      </div>
    </div>
  );
}
